import * as React from 'react';
import { Tree as TreeNative } from '../ui-native/Tree';
import { observer } from 'mobx-react';
import { IReactionDisposer, makeObservable, observable, reaction } from 'mobx';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { cs } from '@shared/utils/utils-classname';
import { Button } from '@shared/ui-react/Button';
import { useIsMobile } from 'design-system';

export interface ITreeCustomizeOptions {
    show?: boolean;
    filterOnly?: boolean;
    filterTreePlaceholder?: string;
    showNavigation?: boolean;
    hideExpandCollapse?: boolean;
    hideExpandOnStart?: boolean;
    elementSelector?: string;
}

/** Props for using TreeToolbar without a native tree (e.g. with the design-system Tree). */
export interface TreeToolbarExternalProps {
    /** Controlled filter text */
    filterText?: string;
    /** Called when the user types in the filter input */
    onFilterChange?: (text: string) => void;
    /** Called when the user clicks Expand All */
    onExpandAll?: () => void;
    /** Called when the user clicks Collapse All */
    onCollapseAll?: () => void;
    /** Whether empty folders are currently hidden */
    hideEmptyFolders?: boolean;
    /** Whether hiding empty folders is forced (not toggleable) */
    hideEmptyFoldersAlways?: boolean;
    /** Called when the user toggles "Hide empty folders" */
    onToggleHideEmptyFolders?: () => void;
    /** Current expand-on-start value */
    expandOnStart?: string;
    /** Called when the user changes expand-on-start */
    onSetExpandOnStart?: (value: string) => void;
    /** Whether the create-folder action should be shown */
    showContextMenuCreateFolder?: boolean;
    /** Called when the user clicks Create Folder in the settings dropdown */
    onOpenCreateFolder?: () => void;
    /** Whether content counts are available (shows hide-empty-folders setting) */
    hasContentCounts?: boolean;
}

/**
 * wrapper component to be paired with design-system Tree, they will both use same breakpoint for mobile view
 * we need to hide expand/collapse buttons on mobile toolbar because tree only shows one folder then
 */
export const TreeToolbarResponsive = (props: React.ComponentProps<typeof TreeToolbar>): JSX.Element => {
    const isMobile = useIsMobile();
    return <TreeToolbar hideExpandCollapse={isMobile} {...props} />;
};

@observer
export class TreeToolbar extends React.Component<
    Partial<
        {
            tree: TreeNative;
        } & ITreeCustomizeOptions &
            TreeToolbarExternalProps
    >
> {
    @observable.ref private tree: TreeNative = null;
    @observable private internalFilterText = '';

    private filterInputEl: HTMLInputElement = null;
    private filterAutorunDispose: IReactionDisposer;
    private initialLoad: boolean = true;
    private dropdown: Dropdown = null;

    private expandOptions = Object.entries({
        none: kuura.getMessage('general.tree.on_start_expand.none'),
        firstLevel: kuura.getMessage('general.tree.on_start_expand.first_level'),
        all: kuura.getMessage('general.tree.on_start_expand.all'),
        restoreState: kuura.getMessage('general.tree.on_start_expand.restore'),
    });

    static defaultProps = {
        showNavigation: false,
    };

    constructor(
        props: Partial<
            {
                tree: TreeNative;
            } & ITreeCustomizeOptions &
                TreeToolbarExternalProps
        >
    ) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        if (this.props.tree) {
            this.initComponent(this.props.tree);
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.tree !== prevProps.tree) {
            if (this.props.tree) {
                this.initComponent(this.props.tree);
            }
        }
    }

    componentWillUnmount(): void {
        if (this.filterAutorunDispose) {
            this.filterAutorunDispose();
        }
    }

    private get isExternalMode(): boolean {
        return !this.props.tree;
    }

    private get resolvedFilterText(): string {
        if (this.isExternalMode) {
            return this.props.filterText ?? '';
        }
        return this.internalFilterText;
    }

    private set resolvedFilterText(value: string) {
        if (this.isExternalMode) {
            this.props.onFilterChange?.(value);
        } else {
            this.internalFilterText = value;
        }
    }

    render() {
        const tree = this.props.tree;
        const isRootSelected = tree?.getSelected()?.isRoot();
        const hasRecent = tree?.recentlySelected.length > 1;

        if (!tree && !this.isExternalMode) return null;

        const OtherCtl = (
            <div className='btn-group' style={{ boxShadow: 'none' }}>
                {this.props.showNavigation && tree && (
                    <>
                        <Dropdown
                            className='ik-button-small ik-button-secondary--subtle file-tree__settings__dropdown-trigger'
                            tooltip={kuura.getMessage('general.tree.recently_selected')}
                            closeOnBodyClick={true}
                            lazyRender={false}
                            disabled={!hasRecent}
                            getBody={() => {
                                const recent = (tree?.recentlySelected || [])?.slice(1);
                                if (!recent.length) return <div className='text-center padded-half'>{__('general.empty')}</div>;
                                return (
                                    <ul className='clickable'>
                                        {tree?.recentlySelected?.slice(1).map((r, i) => (
                                            <li key={i} onClick={() => tree.selectByUuid(r.uuid)}>
                                                {r.label}
                                            </li>
                                        ))}
                                    </ul>
                                );
                            }}
                        >
                            <i className='iki iki-history'></i>
                        </Dropdown>
                        <Button
                            className='ik-button-small ik-button-secondary--subtle'
                            disabled={!hasRecent}
                            icon='iki-chevron-left'
                            tooltip={__('general.back')}
                            onClick={() => tree.goBack()}
                            style={{ paddingLeft: 4, paddingRight: 3, fontSize: 12 }}
                        />
                        <Button
                            className='ik-button-small ik-button-secondary--subtle'
                            disabled={!hasRecent}
                            icon='iki-chevron-right'
                            tooltip={__('general.forward')}
                            onClick={() => tree.goForward()}
                            style={{ paddingLeft: 4, paddingRight: 3, fontSize: 12 }}
                        />
                        <button className='ik-button-small ik-button-secondary--subtle' onClick={this.goUp} disabled={isRootSelected} style={{ paddingLeft: 4, paddingRight: 3 }}>
                            <i className='iki iki-chevron-up'></i>
                        </button>
                    </>
                )}

                {!this.props.hideExpandCollapse && (
                    <>
                        <Button
                            className='file-tree__toolbar__expand-all ik-button-small ik-button-secondary--subtle'
                            tooltip={kuura.getMessage('general.tree.expand_tree')}
                            icon='iki-list-expand'
                            onClick={this.handleExpandAll}
                        />

                        <Button
                            className='file-tree__toolbar__collapse-all ik-button-small ik-button-secondary--subtle'
                            tooltip={kuura.getMessage('general.tree.collapse_tree')}
                            icon='iki-list-collapse'
                            onClick={this.handleCollapseAll}
                        />
                    </>
                )}

                <Dropdown
                    className='ik-button-small ik-button-secondary--subtle file-tree__settings__dropdown-trigger'
                    tooltip={kuura.getMessage('general.tree.tree_settings')}
                    closeOnBodyClick={false}
                    ref={el => (this.dropdown = el)}
                >
                    <i className='iki iki-tree-settings' />
                    <ul>
                        {(tree?.fetchContentCountsFn || this.props.hasContentCounts) && (
                            <>
                                {tree?.hideEmptyFoldersAlways || this.props.hideEmptyFoldersAlways ? (
                                    <li>
                                        <i className='iki iki-hide' />
                                        &nbsp;{kuura.getMessage('general.empty_folders_are_hidden')}
                                    </li>
                                ) : (
                                    <li onClick={this.handleToggleHideEmptyFolders} className='file-tree__settings__hide-empty-folders clickable'>
                                        <input type='checkbox' checked={tree ? tree.hideEmptyFolders : this.props.hideEmptyFolders} readOnly />{' '}
                                        {kuura.getMessage('general.hide_empty_folders')}
                                    </li>
                                )}
                            </>
                        )}

                        {!this.props.hideExpandOnStart && (
                            <li>
                                <div className='flex-column' style={{ padding: 0 }}>
                                    <div style={{ marginBottom: '0.5em' }}>{kuura.getMessage('general.tree.on_start_expand')}:</div>

                                    {this.expandOptions.map(([key, name]) => (
                                        <label className={cs('checkbox-label', 'file-tree__settings__auto-expand-' + key)} key={key}>
                                            <input
                                                type='radio'
                                                checked={(tree ? tree.expandOnStart : this.props.expandOnStart) === key}
                                                onChange={() => this.handleSetExpandOnStart(key)}
                                            />
                                            <span>{name}</span>
                                        </label>
                                    ))}
                                </div>
                            </li>
                        )}

                        {(tree?.showContextMenuCreateFolder || this.props.showContextMenuCreateFolder) && !window.location.href.includes('mobile') && (
                            <li
                                className='clickable'
                                onClick={() => {
                                    if (tree) {
                                        tree.openCreateFolder(tree.getSelected() ?? tree.root);
                                    } else {
                                        this.props.onOpenCreateFolder?.();
                                    }
                                    this.dropdown.close();
                                }}
                            >
                                <i className='iki iki-folder-closed' />
                                &nbsp;{__('js_files.create_folder')}
                            </li>
                        )}
                    </ul>
                </Dropdown>
            </div>
        );

        return (
            <header className={cs('flex-row', 'centered', { 'fade-disable': !tree && !this.isExternalMode })}>
                <input
                    ref={el => (this.filterInputEl = el)}
                    type='text'
                    placeholder={this.props.filterTreePlaceholder || __('general.tree.filter_tree')}
                    style={{ marginLeft: 2 }}
                    onChange={e => {
                        this.resolvedFilterText = e.currentTarget.value;
                    }}
                    onKeyDown={this.handleFilterKeyboard}
                    onBlur={this.handleFilterBlur}
                    value={this.resolvedFilterText}
                    className='search-input'
                />

                {!this.props.filterOnly && OtherCtl}
            </header>
        );
    }

    private goUp = () => {
        const tree = this.props.tree;
        if (tree?.getSelected()?.parent) {
            tree.getSelected().parent.setSelected();
        }
    };

    private initComponent = (tree: TreeNative) => {
        this.tree = tree;
        if (tree.isRendered) {
            this.initTree(tree);
        } else {
            tree.on('render', () => this.initTree(tree));
        }
    };

    private initTree = (tree: TreeNative) => {
        if (!this.initialLoad) return;

        // filter
        if (this.filterAutorunDispose) this.filterAutorunDispose();
        this.filterAutorunDispose = reaction(
            () => this.internalFilterText,
            text => {
                text = this.internalFilterText.trim().toLowerCase();
                if (tree) {
                    tree.filterByNodeText(text);
                }
            },
            { delay: 50 }
        );

        this.initialLoad = false;
    };

    private handleExpandAll = () => {
        if (this.props.onExpandAll) {
            this.props.onExpandAll();
        } else if (this.tree) {
            this.tree.expandAll();
        }
    };

    private handleCollapseAll = () => {
        if (this.props.onCollapseAll) {
            this.props.onCollapseAll();
        } else if (this.tree) {
            this.tree.collapseAll();
        }
    };

    private handleToggleHideEmptyFolders = () => {
        if (this.props.onToggleHideEmptyFolders) {
            this.props.onToggleHideEmptyFolders();
        } else if (this.tree) {
            this.tree.toggleHideEmptyFolders();
        }
    };

    private handleSetExpandOnStart = (key: string) => {
        if (this.props.onSetExpandOnStart) {
            this.props.onSetExpandOnStart(key);
        } else if (this.tree) {
            this.tree.setExpandOnStart(key as any);
        }
    };

    public focusInput = () => {
        if (this.filterInputEl) {
            setTimeout(() => this.filterInputEl.focus(), 50);
        }
    };

    private handleFilterBlur = e => {
        if (this.tree) {
            this.tree.unHighlightHighlighted();
        }
    };

    private handleFilterKeyboard = e => {
        if (this.tree) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                this.tree.highlightNext();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                this.tree.highlightPrevious();
            } else if (e.key === 'Enter') {
                this.tree.selectHighlighted();
            }
        }

        if (e.key === 'Escape') {
            this.resolvedFilterText = '';
            this.focusInput();
            if (this.tree) {
                this.tree.unHighlightHighlighted();
            }
        }
    };
}
